import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Clock, Eye, ArrowRight } from "@phosphor-icons/react";
import { api } from "../lib/api";
import Seo from "../components/Seo";

/**
 * Public single-post page. Renders markdown content with article-grade typography
 * and emits per-post SEO + OpenGraph tags.
 */
export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await api.get(`/blog/${slug}`);
        if (!cancelled) setPost(r.data);
      } catch (e) {
        if (!cancelled) {
          if (e.response?.status === 404) setError("Post not found.");
          else setError("Failed to load post.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-app text-primary-fg flex items-center justify-center">
        <div className="text-xs font-mono uppercase tracking-widest text-muted-fg animate-pulse" data-testid="blog-post-loading">
          /// Loading…
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-app text-primary-fg">
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="text-sm text-muted-fg mb-6" data-testid="blog-post-error">{error}</div>
          <Link to="/blog" className="text-xs font-mono uppercase tracking-widest text-pos hover:underline">
            ← Back to all posts
          </Link>
        </div>
      </div>
    );
  }

  const wc = (post.content_markdown?.split(/\s+/).length) || 200;
  const minutes = Math.max(2, Math.round(wc / 200));
  const dateStr = (() => {
    try { return new Date(post.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return ""; }
  })();

  return (
    <div className="min-h-screen bg-app text-primary-fg">
      <Seo
        title={`${post.title} · Breakout Scanner Blog`}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.cover_image}
      />
      <article className="max-w-2xl mx-auto px-4 py-10 sm:py-14" data-testid="blog-post-article">
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-fg hover:text-primary-fg transition mb-7"
          data-testid="blog-post-back"
        >
          <ArrowLeft size={11} weight="bold" />
          All posts
        </Link>

        <div className="flex items-center gap-4 text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-4">
          {dateStr && <span>{dateStr}</span>}
          <span className="flex items-center gap-1"><Clock size={10} /> {minutes} min read</span>
          <span className="flex items-center gap-1"><Eye size={10} /> {post.views} views</span>
        </div>

        <h1 className="text-3xl sm:text-4xl font-medium tracking-tight leading-tight mb-4" data-testid="blog-post-title">
          {post.title}
        </h1>
        <p className="text-base text-secondary-fg leading-relaxed mb-8 italic border-l-2 border-pos/40 pl-4">
          {post.excerpt}
        </p>

        {post.cover_image && (
          <img
            src={post.cover_image}
            alt={post.title}
            className="w-full rounded-sm mb-8 border border-subtle"
          />
        )}

        <div
          className="prose-bs leading-relaxed text-[15px] text-primary-fg space-y-5"
          data-testid="blog-post-body"
        >
          <ReactMarkdown
            components={{
              h1: ({ node, ...p }) => <h1 className="text-2xl sm:text-3xl font-medium mt-10 mb-4 tracking-tight" {...p} />,
              h2: ({ node, ...p }) => <h2 className="text-xl sm:text-2xl font-medium mt-9 mb-3 tracking-tight" {...p} />,
              h3: ({ node, ...p }) => <h3 className="text-lg font-medium mt-7 mb-2 tracking-tight" {...p} />,
              p:  ({ node, ...p }) => <p className="text-[15px] leading-relaxed text-secondary-fg" {...p} />,
              ul: ({ node, ...p }) => <ul className="list-disc list-outside ml-5 space-y-1.5 text-secondary-fg" {...p} />,
              ol: ({ node, ...p }) => <ol className="list-decimal list-outside ml-5 space-y-1.5 text-secondary-fg" {...p} />,
              li: ({ node, ...p }) => <li className="leading-relaxed" {...p} />,
              a:  ({ node, ...p }) => <a className="text-pos underline underline-offset-2 hover:text-pos/80" {...p} />,
              strong: ({ node, ...p }) => <strong className="text-primary-fg font-semibold" {...p} />,
              em: ({ node, ...p }) => <em className="text-primary-fg" {...p} />,
              code: ({ inline, ...p }) =>
                inline
                  ? <code className="text-pos bg-pos-soft/20 px-1.5 py-0.5 rounded text-[13px] font-mono" {...p} />
                  : <code className="block bg-surface border border-subtle rounded-sm p-4 font-mono text-[13px] overflow-x-auto" {...p} />,
              blockquote: ({ node, ...p }) => (
                <blockquote className="border-l-2 border-info/50 bg-info-soft/10 px-4 py-3 my-4 text-secondary-fg italic" {...p} />
              ),
              table: ({ node, ...p }) => (
                <div className="overflow-x-auto my-4">
                  <table className="w-full text-sm border border-subtle" {...p} />
                </div>
              ),
              th: ({ node, ...p }) => <th className="text-left px-3 py-2 border-b border-subtle bg-surface text-[12px] font-mono uppercase tracking-widest text-muted-fg" {...p} />,
              td: ({ node, ...p }) => <td className="px-3 py-2 border-b border-subtle/50 text-secondary-fg" {...p} />,
              hr: () => <hr className="border-subtle my-8" />,
            }}
          >
            {post.content_markdown}
          </ReactMarkdown>
        </div>

        {post.tags?.length > 0 && (
          <div className="mt-10 pt-6 border-t border-subtle">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-2">Tags</div>
            <div className="flex flex-wrap gap-2">
              {post.tags.map((t) => (
                <span key={t} className="text-[11px] font-mono text-pos/80 border border-pos/30 px-2 py-1 rounded-sm">
                  #{t}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="mt-12 border border-pos/30 bg-pos-soft/10 rounded-sm p-5">
          <div className="text-[11px] font-mono uppercase tracking-widest text-pos mb-2">Ready to apply this?</div>
          <h3 className="text-lg font-medium mb-2">Find live breakouts on NSE today</h3>
          <p className="text-sm text-secondary-fg leading-relaxed mb-4">
            Our scanner runs every market close — get fresh multi-year breakouts,
            cup & handle setups, and AI fundamentals delivered to your dashboard.
          </p>
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            data-testid="blog-post-cta"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-sm text-xs font-medium"
            style={{ background: "hsl(var(--pos))", color: "hsl(var(--bg))" }}
          >
            Try the scanner — ₹5 / 5 days
            <ArrowRight size={12} weight="bold" />
          </button>
        </div>
      </article>
    </div>
  );
}
